/**
 * The valley, full screen, running on a parent's own account.
 *
 * This is the wall display's job done without the wall display: a screen in
 * the kitchen showing the family's valley, the balance and what everyone is
 * saving for, kept current all day. The difference is whose credential it
 * runs on. A linked display holds a `display`-scoped device token; this holds
 * the parent's own session, because a parent who wants the valley up on their
 * laptop should not have to link a device to get it.
 *
 * THE SUN AND MOON ARE THE WAY OUT, and that is ported rather than invented.
 * On the wall display the celestial body is literally
 * `<a id="celestial" href="/parents">` -- the discreet, thematic entry to the
 * grown-ups' side, with no chrome anywhere else on the screen. The same idea
 * here, with one addition: it asks for the PIN.
 *
 * WHY IT ASKS. The point of this screen is to be LEFT RUNNING, which means
 * leaving an authenticated session on a screen in a room with children in it.
 * Without a gate, the way back to the account -- the ledger, the family code,
 * the delete button -- is one tap on the prettiest thing on the display.
 *
 * WHAT THE GATE IS, stated honestly. It is a UI gate on a live session, not a
 * lock. The tokens stay in memory so the valley can keep reading; the PIN is
 * what stands in front of the account. A reload does not escape it -- the
 * mode is remembered, see `valleyMode` in App -- but somebody with devtools
 * and the will to use them is not who this stops. It stops the child who
 * wanders up to the screen, which is the threat this product actually has.
 * A parent who wants the stronger thing should leave this and lock.
 *
 * THE BUILDER IS TOP RIGHT, beside the sun. Children are who stand at this
 * screen, so it asks whose character, opens the display's builder, and
 * QUEUES the look for approval (`draftLook`) exactly as a child's own page
 * does -- the parent's session behind the screen is not a way around that.
 *
 * THE IDLE LOCK IS SUSPENDED while this is on screen, deliberately. An
 * ambient display that logs itself out after ten quiet minutes is not an
 * ambient display -- going quiet is the entire point of it.
 */

import { useEffect, useState } from 'react';
import { useLiveState } from '../family/useLiveState';
import { isNight } from '../valley/scene';
import { LivingValley } from '../valley/LivingValley';
import { BalanceHud, GoalHud, JournalHud } from '../valley/ValleyHud';
import { Celestial } from '../valley/Celestial';
import { currentAccount } from '../auth/cognito';
import { hasPin, unlockWithPin, MAX_ATTEMPTS } from '../auth/pin';
import { PinEntry } from '../components/PinEntry';
import { draftLook, type FamilyState, type Person } from '../family/state';
import { Builder } from '../avatar/Builder';
import { BuilderLaunch } from '../avatar/BuilderLaunch';
import { Character } from '../avatar/Character';
import { eligible } from '../avatar/residents';
import { ResidentCard } from '../valley/ResidentCard';
import { native } from '../native/platform';

/**
 * How often the clock is re-read, for a screen that may be up for days.
 *
 * Everything else about the scene changes when the family does. The HOUR
 * changes on its own, and a valley that is still bright at midnight because
 * nothing re-rendered is the most obvious possible bug on an always-on
 * screen. A minute is far finer than the 19:00/06:00 boundary needs and costs
 * one state update.
 */
const CLOCK_MS = 60_000;

export function FullValley({
  familyId,
  familyKey,
  initial,
  onExit,
  onLockedOut,
  onForgotPin,
}: {
  familyId: string;
  familyKey: CryptoKey;
  initial: FamilyState;
  /** The PIN was right, or there was none to ask for. */
  onExit: () => void;
  /** Too many wrong PINs. The record is gone; only a full sign-in returns. */
  onLockedOut: () => void;
  /**
   * They cannot remember it. Leave the valley and sign in with a password.
   *
   * A separate way out from `onLockedOut`, because the destinations differ:
   * that one has lost its record and goes to the picker, this one still has a
   * perfectly good PIN that its owner has forgotten and goes straight to
   * Cognito. Both have to clear valley mode on the way, or signing in lands
   * back on the screen they were trying to leave.
   */
  onForgotPin: () => void;
}) {
  // In the app: no status bar over the painting, and the screen stays on
  // while the valley is up -- it is meant to sit on a stand. A browser gets
  // the Fullscreen API from App.tsx instead.
  useEffect(() => {
    const shell = native();
    shell?.setImmersive?.(true);
    shell?.keepAwake?.(true);
    return () => {
      shell?.setImmersive?.(false);
      shell?.keepAwake?.(false);
    };
  }, []);
  const [asking, setAsking] = useState(false);
  const [view, setView] = useState(() => measure());
  /** Whose character, before the builder opens. */
  const [choosing, setChoosing] = useState(false);
  const [styling, setStyling] = useState<Person | null>(null);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState('');
  const [sent, setSent] = useState('');
  /** Whose card is open, from tapping them in the valley. */
  const [card, setCard] = useState<string | null>(null);
  const covered = asking || choosing || Boolean(styling);
  // Polling pauses behind the PIN prompt: nobody is looking at the valley,
  // and a re-render under the dialog is a re-render nobody asked for.
  const { state, refresh } = useLiveState(familyId, familyKey, initial, !covered);

  useEffect(() => {
    const tick = () => setView(measure());
    const timer = window.setInterval(tick, CLOCK_MS);
    window.addEventListener('resize', tick);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', tick);
    };
  }, []);

  // The painting stage is LivingValley's to work out; all this screen decides
  // is whether it is night, from this device's own clock.
  const night = isNight(view.hour);
  // Everyone approved with a character, for the faces beside rows and goals.
  const faces = Object.fromEntries(
    state.people
      .filter((person) => person.status === 'approved' && person.avatar)
      .map((person) => [person.id, person.avatar]),
  );

  // Who stands in the valley: everybody approved with a character.
  const residents = eligible(state.people).map((p) => ({ id: p.id, name: p.name, avatar: p.avatar }));
  const cardPerson = card ? state.people.find((p) => p.id === card && p.status === 'approved') : undefined;
  // Who can have a character made here: everybody in the family.
  const people = state.people.filter((person) => person.status !== 'removed');

  useEffect(() => {
    if (!sent) return;
    const timer = window.setTimeout(() => setSent(''), 6000);
    return () => window.clearTimeout(timer);
  }, [sent]);

  function openBuilder(): void {
    setSent('');
    if (people.length === 1) setStyling(people[0]!);
    else setChoosing(true);
  }

  if (styling) {
    return (
      <Builder
        initial={styling.pending?.avatar ?? styling.avatar}
        name={styling.name}
        saveLabel={styling.avatar ? 'Send updated look for approval' : 'Send for parent approval'}
        busy={sending}
        error={problem}
        note="A parent reviews your character before it appears in the valley. You can update it later."
        onSave={(spec, name) => {
          setSending(true);
          setProblem('');
          void draftLook(familyId, familyKey, styling, spec, name)
            .then(async () => {
              setStyling(null);
              setSent('Sent! A grown-up will have a look.');
              await refresh().catch(() => {});
            })
            .catch((error: unknown) =>
              setProblem(error instanceof Error ? error.message : 'That did not send.'),
            )
            .finally(() => setSending(false));
        }}
        onCancel={() => {
          setStyling(null);
          setProblem('');
        }}
      />
    );
  }

  function leave(): void {
    const account = currentAccount();
    // Nothing to ask. Consistent with the rule in `auth/idle.ts`: an account
    // with no PIN is never held behind one, because the only gate available
    // would be a Cognito password and that was already judged too much.
    if (!account || !hasPin(account.sub)) {
      onExit();
      return;
    }
    setAsking(true);
  }

  return (
    <main
      // A landmark, so a screen reader can find the whole valley (audit).
      aria-label={state.familyName ? `${state.familyName}'s valley` : 'The valley'}
      className={[night ? 'full-valley is-night' : 'full-valley', card ? 'card-open' : ''].join(' ').trim()}
      onClick={(event) => {
        // The goals and the journal float over the meadow, so a child behind
        // one was unreachable -- visible and not pressable. `residentAt` in
        // the display's scene.js; ported for the same reason.
        const target = event.target as Element;
        if (target.closest('button, a, .resident-card, .celestial-gate, .avatar-resident')) return;
        if (typeof document.elementsFromPoint !== 'function') return;
        for (const node of document.elementsFromPoint(event.clientX, event.clientY)) {
          const hit = node.closest?.('.avatar-resident[data-person]') as HTMLElement | null;
          if (hit?.dataset.person) {
            setCard(hit.dataset.person);
            return;
          }
        }
        if (card) setCard(null);
      }}
    >
      {/* Paused behind the PIN prompt: nobody is watching the valley then,
          and a phone left on the counter should not spend battery on it. */}
      <LivingValley
        balance={state.balance}
        {...(state.gardenTarget ? { target: state.gardenTarget } : {})}
        night={night}
        paused={covered}
        people={residents}
        onPick={(id) => setCard(id)}
      >
        {/* The display's three pieces of chrome, ported with its layout. */}
        {/* Which family this is, at the top of the balance panel. */}
        <BalanceHud cents={state.balance} name={state.familyName} />
        <GoalHud goals={state.goals} balance={state.balance} faces={faces} />
        <JournalHud rows={state.entries} faces={faces} />

        <Celestial balance={state.balance} onPress={leave} />
        {people.length ? (
          <BuilderLaunch spec={undefined} label="Make your character" onOpen={openBuilder} />
        ) : null}
        {sent ? <p className="valley-sent" role="status">{sent}</p> : null}

        {card && cardPerson ? (
          <ResidentCard
            familyId={familyId}
            familyKey={familyKey}
            person={cardPerson}
            setAside={state.goals
              .filter((goal) => goal.status === 'active')
              .reduce((sum, goal) => sum + (goal.funds[cardPerson.id] ?? 0), 0)}
            allowance={state.allowance[cardPerson.id] ?? null}
            onClose={() => setCard(null)}
          />
        ) : null}

        {choosing ? (
          <WhoIsIt
            people={people}
            onPick={(person) => {
              setChoosing(false);
              setStyling(person);
            }}
            onCancel={() => setChoosing(false)}
          />
        ) : null}

        {asking ? (
          <PinGate
            onOpened={onExit}
            onGone={onLockedOut}
            onForgot={onForgotPin}
            onCancel={() => setAsking(false)}
          />
        ) : null}
      </LivingValley>
    </main>
  );
}

/** Whose character? Every face in the family, big enough for a small finger. */
function WhoIsIt({
  people,
  onPick,
  onCancel,
}: {
  people: Person[];
  onPick: (person: Person) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onCancel]);
  return (
    <div className="celestial-gate" role="dialog" aria-modal="true" aria-labelledby="who-is-it">
      <div className="celestial-gate-card who-is-it">
        <h2 id="who-is-it">Whose character?</h2>
        <div className="who-is-it-faces">
          {people.map((person) => (
            <button key={person.id} type="button" onClick={() => onPick(person)}>
              <Character spec={person.pending?.avatar ?? person.avatar} size={72} />
              <span>{person.name || 'Somebody'}</span>
            </button>
          ))}
        </div>
        <div className="actions">
          <button type="button" onClick={onCancel}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The PIN, asked for over the valley.
 *
 * `unlockWithPin` rather than a bespoke check, and the refresh token it hands
 * back is deliberately DISCARDED: the session is still live, so there is
 * nothing to exchange. What is wanted is the one thing only that function
 * does properly -- decrypt against the AES-GCM tag, spend an attempt first,
 * and destroy the record after five. Writing a second, looser check here is
 * how a cap gets quietly halved.
 */
function PinGate({
  onOpened,
  onGone,
  onForgot,
  onCancel,
}: {
  onOpened: () => void;
  onGone: () => void;
  onForgot: () => void;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function attempt(entered: string): Promise<void> {
    const account = currentAccount();
    if (!account || busy) return;
    setBusy(true);
    setError('');
    try {
      const outcome = await unlockWithPin(account.sub, entered);
      if (outcome.status === 'ok') {
        onOpened();
        return;
      }
      if (outcome.status === 'gone') {
        onGone();
        return;
      }
      setPin('');
      setError(
        outcome.left === 1
          ? 'Not quite. One more try before this account needs its password.'
          : `Not quite. ${outcome.left} tries left.`,
      );
    } catch {
      setError('That did not work. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="celestial-gate">
      <form
        className="celestial-gate-card"
        onSubmit={(event) => {
          event.preventDefault();
          void attempt(pin);
        }}
      >
        <h2>Grown-ups</h2>
        <p>Enter your PIN to leave the valley.</p>
        <PinEntry
          label="PIN"
          value={pin}
          onChange={setPin}
          onComplete={(entered) => void attempt(entered)}
          autoFocus
          disabled={busy}
        />
        {error ? <p className="error">{error}</p> : null}
        <p className="footnote">
          After {MAX_ATTEMPTS} wrong tries this account signs in with its
          password again.
        </p>
        <div className="actions">
          <button aria-busy={busy} className="primary" type="submit" disabled={busy || pin.length === 0}>
            {busy ? 'Checking…' : 'Leave the valley'}
          </button>
          <button type="button" disabled={busy} onClick={onCancel}>
            Stay here
          </button>
        </div>

        <button type="button" className="text-button" disabled={busy} onClick={onForgot}>
          Forgot your PIN?
        </button>
      </form>
    </div>
  );
}

/** The hour is read from the DEVICE, for the reason Valley.tsx gives. */
function measure(): { hour: number; width: number; height: number } {
  return {
    hour: new Date().getHours(),
    width: window.innerWidth,
    height: window.innerHeight,
  };
}
